/** Black & White adjustment — pure math + types, no React, no Fabric.
 *
 *  Converts a color image to grayscale via six per-hue-band weight
 *  sliders (Reds / Yellows / Greens / Cyans / Blues / Magentas).
 *  Each slider controls how bright the matching color band appears
 *  in the output gray: higher = brighter, lower = darker, negative =
 *  darker than the achromatic component.
 *
 *  Hue-band approach (Photoshop-faithful): every pixel is decomposed
 *  into its achromatic floor (`min(r, g, b)`) and chromatic remainder
 *  (`max - min`). The chromatic remainder is scaled by a band weight
 *  determined by the pixel's hue position within the six bands. The
 *  output gray is `floor + weight × chroma`. Achromatic pixels
 *  (white, black, grays) pass through unchanged regardless of slider
 *  positions — this matches Photoshop's behavior and keeps highlights
 *  and shadows neutral.
 *
 *  Default weights mirror Photoshop's "Default" preset (R=40, Y=60,
 *  G=40, C=60, B=20, M=80) and are also what the PSD round-trip
 *  mapper falls back to. Slider range is -200..300 (signed percent).
 *
 *  Tint (the Photoshop "tint" checkbox + color picker) is NOT modeled
 *  in v1 — the PSD mapper flags tinted adjustments as lossy so the
 *  user knows the editor view drops tint information. */

export interface BlackAndWhiteWeights {
  reds: number;
  yellows: number;
  greens: number;
  cyans: number;
  blues: number;
  magentas: number;
}

export interface BlackAndWhiteValue {
  weights: BlackAndWhiteWeights;
}

/** Photoshop's "Default" preset. Reused by the PSD mapper's fallback
 *  when a PSD layer omits per-band values. */
export const identityBlackAndWhiteValue = (): BlackAndWhiteValue => ({
  weights: { reds: 40, yellows: 60, greens: 40, cyans: 60, blues: 20, magentas: 80 },
});

/** Pure pixel transform — returns a fresh ImageData. Tests use this
 *  variant; the canvas-bound wrapper does the putImageData. */
export function applyBlackAndWhitePixels(source: ImageData, value: BlackAndWhiteValue): ImageData {
  const w = source.width;
  const h = source.height;
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  const wR = value.weights.reds / 100;
  const wY = value.weights.yellows / 100;
  const wG = value.weights.greens / 100;
  const wC = value.weights.cyans / 100;
  const wB = value.weights.blues / 100;
  const wM = value.weights.magentas / 100;

  for (let i = 0; i < sd.length; i += 4) {
    const r = sd[i] / 255;
    const g = sd[i + 1] / 255;
    const b = sd[i + 2] / 255;

    let max = r;
    let min = r;
    if (g > max) max = g;
    if (b > max) max = b;
    if (g < min) min = g;
    if (b < min) min = b;
    const chroma = max - min;

    let gray: number;
    if (chroma === 0) {
      // Achromatic — pass through. Whites stay white, blacks stay
      // black; sliders only touch chromatic pixels.
      gray = max;
    } else {
      // Locate the pixel within one of the six 60° hue bands and
      // linearly interpolate between the band's two endpoint weights.
      // `t` is the position within the band, 0 at the lower endpoint
      // and 1 at the upper. R-Y / Y-G / G-C / C-B / B-M / M-R, walking
      // the hue circle.
      let w1: number;
      let w2: number;
      let t: number;
      if (max === r) {
        if (g >= b) {
          // R → Y. Pure red at t=0, pure yellow at t=1.
          w1 = wR;
          w2 = wY;
          t = (g - b) / chroma;
        } else {
          // M → R. Pure magenta at t=0, pure red at t=1.
          w1 = wM;
          w2 = wR;
          t = 1 - (b - g) / chroma;
        }
      } else if (max === g) {
        if (b > r) {
          // G → C. Pure green at t=0, pure cyan at t=1.
          w1 = wG;
          w2 = wC;
          t = (b - r) / chroma;
        } else {
          // Y → G. Pure yellow at t=0, pure green at t=1.
          w1 = wY;
          w2 = wG;
          t = 1 - (r - b) / chroma;
        }
      } else {
        if (r > g) {
          // B → M. Pure blue at t=0, pure magenta at t=1.
          w1 = wB;
          w2 = wM;
          t = (r - g) / chroma;
        } else {
          // C → B. Pure cyan at t=0, pure blue at t=1.
          w1 = wC;
          w2 = wB;
          t = 1 - (g - r) / chroma;
        }
      }
      const bandWeight = w1 * (1 - t) + w2 * t;
      gray = min + bandWeight * chroma;
    }

    const clamped = gray < 0 ? 0 : gray > 1 ? 1 : gray;
    const g8 = (clamped * 255 + 0.5) | 0;
    out[i] = g8;
    out[i + 1] = g8;
    out[i + 2] = g8;
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyBlackAndWhiteToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: BlackAndWhiteValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyBlackAndWhitePixels(source, value), 0, 0);
}
